import { useTranslation } from 'react-i18next';
import BulkEditor from './BulkEditor';
import LocationAwareSearch from '../../../../components/LocationAwareSearch';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from '../../../../hooks';
import { useAsync } from 'react-async';
import { isEqual } from 'lodash';
import { post } from '../../../../api/node';
import { KoobInputSegmented, KoobPagination } from '@koob/ui';
import {
  ExperienceTypeEnum,
  OrderColumnEnum,
  OrderDirectionEnum,
} from '@koob/enums';
import { getImageUrl } from '@koob/utils';
import { useKoobStore } from '../../../../store';
import useBulkEditExcludedIds from './useBulkEditExcludedIds';
import { matchesSearchWords, toSearchWords } from '@koob/text-search';

const getList = async ({ filters }) => {
  return await post('/experiences/liste', filters);
};

export default function BulkEditorExperiences() {
  const { t } = useTranslation('toConnectionRequest');
  const [{ search }] = useSearchParams();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(48);
  const [type, setType] = useState([]);
  const [totalPages, setTotalPages] = useState();

  const toConnectionRequestModel = useKoobStore(
    (state) => state.toConnectionRequestModel,
  );
  const [showOnlySelected, setShowOnlySelected] = useState(
    toConnectionRequestModel ? true : null,
  );

  const excludedIds = useBulkEditExcludedIds({
    fieldName: 'toConnectionRequestExperiences',
    showOnlySelected,
  });
  const excludedIdsKey = excludedIds?.join(',');

  const requestFilters = {
    search: search ?? '',
    types: type && type != null ? [type] : [],
    ...(excludedIds && { excludedExperiences: excludedIds }),
    perPage: perPage,
    page,
    direction: OrderDirectionEnum.DESC,
    order: OrderColumnEnum.UPDATED_AT,
    shouldSearchAdditionalFields: false,
  };

  const { data, isLoading: loading } = useAsync({
    promiseFn: getList,
    filters: requestFilters,
    watchFn: (props, prevProps) => {
      return !isEqual(props.filters, prevProps.filters);
    },
    onResolve: (response) => {
      setTotalPages(response?.headers?.xTotalPages);
    },
  });

  useEffect(() => {
    setPage(1);
  }, [search, type, excludedIdsKey]);

  const experiences = data?.data ?? [];
  const totalCount = data?.headers?.xTotalCount;

  // Only the "selected" view filters in the browser: it lists the local
  // selection, which the server never sees. Server pages are shown as they
  // come, and the search mirrors the server's (name or KOOB id, KD-3530).
  const searchWords = useMemo(() => toSearchWords(search), [search]);
  const filterResult = useCallback(
    (item) => {
      if (type && type.length > 0 && String(item.description) !== String(type))
        return false;
      return matchesSearchWords(searchWords, [item.name], item.id);
    },
    [type, searchWords],
  );

  return (
    <BulkEditor
      title={t('bulkEdit.editor.experiencesTitle')}
      filters={
        <div className="flex-col space-y-3">
          <LocationAwareSearch
            name="search"
            placeholder={t('misc:searchByNameOrId')}
          />

          <KoobInputSegmented
            options={[
              { label: t('experiences:state.all'), value: null },
              { label: t('experiences:transfer'), value: 'Transfer' },
              { label: t('experiences:types.activity'), value: 'Activity' },
              { label: t('experiences:types.extra'), value: 'Extra' },
              { label: t('experiences:types.program'), value: 'Program' },
            ]}
            value={type}
            onChange={(value) => setType(value)}
          />
        </div>
      }
      loading={loading}
      results={experiences.map((experience) => ({
        id: experience.id,
        image: getImageUrl(experience),
        name: experience.name,
        description: experience.isExtra
          ? ExperienceTypeEnum.EXTRA
          : experience.experienceType,
        state: experience.state,
      }))}
      type="experience"
      totalCount={
        excludedIds ? Number(totalCount ?? 0) + excludedIds.length : totalCount
      }
      filterResult={filterResult}
      onShowOnlySelectedChange={setShowOnlySelected}
    >
      {experiences?.length > 0 && (
        <KoobPagination
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          totalCount={totalCount}
          perPage={perPage}
          setPerPage={setPerPage}
        />
      )}
    </BulkEditor>
  );
}
